import React from "react";
import { Box, Typography, Button, Paper, Stack } from "@mui/material";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Storefront from "@mui/icons-material/Storefront";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import { getCategoryMeta } from "../config/productCategories";
import CategoryIconArt from "./categoryIcons/CategoryIconArt";

const CategoryComingSoonPanel = ({
  categoryName,
  primaryBlue,
  glassStyles,
  onBrowseAll,
  onNotifyInterest,
}) => {
  const meta = getCategoryMeta(categoryName) || {
    name: categoryName,
    tagline: "We're adding products to this category very soon.",
    gradient: ["#1E40AF", "#60A5FA"],
  };
  const [g0, g1] = meta.gradient;

  return (
    <Paper
      elevation={0}
      sx={{
        ...glassStyles,
        position: "relative",
        overflow: "hidden",
        borderRadius: "24px",
        p: { xs: 3, sm: 5 },
        bgcolor: "rgba(255, 255, 255, 0.45)",
        backdropFilter: "blur(24px)",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 15% 20%, ${g0}22 0%, transparent 45%),
            radial-gradient(circle at 85% 75%, ${g1}28 0%, transparent 50%)
          `,
          pointerEvents: "none",
        }}
      />

      <Stack
        spacing={3}
        alignItems="center"
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: "28px",
            background: `linear-gradient(135deg, ${g0}, ${g1})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 16px 40px ${g0}44`,
            animation: "floatIcon 4s ease-in-out infinite",
            "@keyframes floatIcon": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-6px)" },
            },
          }}
        >
          <CategoryIconArt categoryName={categoryName} size={52} />
        </Box>

        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 2,
              fontWeight: 700,
              color: primaryBlue?.[600] || "#002366",
            }}
          >
            {meta.name}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mt: 0.5,
              background: `linear-gradient(45deg, ${primaryBlue?.[800] || "#0B1C48"}, ${g0})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 1.5,
              maxWidth: 480,
              mx: "auto",
              color: "rgba(21, 101, 192, 0.85)",
              lineHeight: 1.6,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
            }}
          >
            {meta.tagline}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ width: "100%", maxWidth: 420, justifyContent: "center" }}
        >
          <Box
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.55)",
              border: "1px solid rgba(255,255,255,0.6)",
            }}
          >
            <AutoAwesome sx={{ color: g0, mb: 0.5 }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Curating quality
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hand-picked items for this aisle
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.55)",
              border: "1px solid rgba(255,255,255,0.6)",
            }}
          >
            <Storefront sx={{ color: g1, mb: 0.5 }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Launching here first
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You&apos;ll see them in this category
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Storefront />}
            onClick={onBrowseAll}
            sx={{
              px: 3,
              py: 1.25,
              fontWeight: 700,
              borderRadius: 3,
              background: `linear-gradient(45deg, ${primaryBlue?.[600] || "#002366"}, ${primaryBlue?.[400] || "#0072CE"})`,
              boxShadow: "0 8px 24px rgba(0, 114, 206, 0.35)",
            }}
          >
            Browse all products
          </Button>
          {onNotifyInterest && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<NotificationsActiveOutlined />}
              onClick={() => onNotifyInterest(meta.name)}
              sx={{
                px: 3,
                py: 1.25,
                fontWeight: 600,
                borderRadius: 3,
                borderColor: primaryBlue?.[300],
                color: primaryBlue?.[700],
              }}
            >
              I&apos;m interested
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CategoryComingSoonPanel;
